import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Row, Col, Select, Carousel, Modal } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { getImageFromUrl } from '../../Services/PIM/ImageService'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { AllImageOption, ImageType, ImageTypeTranslations } from '../../Constants/Configuration'
import { hideImageModal } from '../../Slices/Kiosk/ArticleSlice'
import { filterGeneralDistinct, renderTranslation } from '../../Helpers/GenericHelpers'
import { useAppDispatch, useAppSelector } from '../../hooks'

const { Option } = Select

const NextArrow = props => (
  <div {...props}>
    <RightOutlined className='carousel-right-caret' />
  </div>
)

const PrevArrow = props => (
  <div {...props}>
    <LeftOutlined className='carousel-left-caret' />
  </div>
)

function ImagesGallery() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const missingImg = useAppSelector(state => state.present.language.missingImg)
  const modalInfo = useAppSelector(state => state.present.article.imageModalInfo)
  const article = useAppSelector(state => state.present.article.value)

  const [currentSlide, setCurrentSlide] = useState(0)
  const [currentImgType, setCurrentImgType] = useState(AllImageOption)

  useEffect(() => {
    setCurrentImgType(AllImageOption)
    setCurrentSlide(0)
  }, [modalInfo])

  const handlePhotoSelectChange = useCallback(value => {
    setCurrentImgType(value)
  }, [])

  const hideModal = useCallback(() => {
    dispatch(hideImageModal())
  }, [dispatch])

  const selectedResult = useMemo(() => {
    return article && modalInfo.articleKey !== null
      ? article.results.filter(article => article.key === modalInfo.articleKey)
      : []
  }, [article, modalInfo.articleKey])

  const imageTypeAvailable = useMemo(() => {
    return selectedResult.length && selectedResult[0].images && selectedResult[0].images.length !== 0
      ? selectedResult[0].images
          .map((element, index) => {
            return element.type
          })
          .filter(filterGeneralDistinct)
      : []
  }, [selectedResult])

  const imageGallery = useMemo(() => {
    const gallery = []
    if (selectedResult.length && selectedResult[0].images && selectedResult[0].images.length !== 0) {
      const images =
        currentImgType !== AllImageOption
          ? selectedResult[0].images.filter(element => element.type === currentImgType)
          : selectedResult[0].images
      images.forEach(element => {
        const image = getImageFromUrl(element.url)
        if (image && element.default) {
          gallery.splice(0, 0, image)
        } else {
          gallery.push(image)
        }
      })
    }
    return gallery.filter(filterGeneralDistinct)
  }, [currentImgType, selectedResult])

  const setCurrentModalSlide = useCallback(currentSlide => {
    setCurrentSlide(currentSlide)
  }, [])

  const modalCarouselRef = useRef(null)

  return (
    <Modal
      open={modalInfo.showModal}
      footer={null}
      onCancel={hideModal}
      style={{ top: '64px' }}
      width={1000}
      destroyOnClose={true}
    >
      <Row style={{ height: '100%' }}>
        <Col span={18} className='modal-carousel-container'>
          <Carousel
            arrows
            nextArrow={<NextArrow />}
            prevArrow={<PrevArrow />}
            dots={false}
            ref={modalCarouselRef}
            className='modal-carousel'
            initialSlide={currentSlide}
            beforeChange={(current, next) => setCurrentModalSlide(next)}
          >
            {imageGallery.length !== 0 ? (
              imageGallery.map(item => {
                return (
                  <div className='image-container' key={imageGallery.indexOf(item)}>
                    <img
                      src={item}
                      className='carousel-responsive-img'
                      alt=''
                      onError={e => {
                        const target = e.target as HTMLImageElement
                        target.onerror = null
                        target.src = missingImg[TranslationKeys.notFoundImgPath]
                      }}
                    />
                  </div>
                )
              })
            ) : (
              <div className='image-container missing-img'>
                <img src={missingImg[TranslationKeys.missingImgPath]} className='responsive-img' alt='' />
              </div>
            )}
          </Carousel>
        </Col>
        <Col span={6} className=' overflow-hidden pa-2 '>
          <Row>
            <Col span={24} className='spacing-bottom-30'>
              <Select
                value={currentImgType}
                className='w-100'
                onChange={handlePhotoSelectChange}
                disabled={
                  selectedResult.length && selectedResult[0].images && selectedResult[0].images.length === 0
                    ? true
                    : false
                }
              >
                {imageTypeAvailable.map(type => {
                  return (
                    <Option value={type}>
                      {Object.values(ImageType).indexOf(type) !== -1
                        ? ImageTypeTranslations.map(element => {
                            return element.key === type ? renderTranslation(language, element.translation) : null
                          })
                        : type}
                    </Option>
                  )
                })}
                <Option value={AllImageOption}>
                  {renderTranslation(
                    language,
                    ImageTypeTranslations.find(type => {
                      return type.key === AllImageOption
                    })?.translation,
                  )}
                </Option>
              </Select>
            </Col>
          </Row>
          <Row className='modal-image-vertical-list'>
            <Col span={24}>
              <Row gutter={[16, 16]} justify='center' align='middle'>
                {imageGallery.length !== 0 ? (
                  imageGallery.map(item => {
                    return (
                      <Col
                        span={imageGallery.length > 6 ? 8 : 12}
                        key={imageGallery.indexOf(item)}
                        className='modal-image-spacing '
                      >
                        <img
                          src={item}
                          className={
                            currentSlide === imageGallery.indexOf(item) ? 'responsive-img active' : 'responsive-img'
                          }
                          onClick={() => {
                            modalCarouselRef.current.goTo(imageGallery.indexOf(item))
                          }}
                          onError={e => {
                            const target = e.target as HTMLImageElement
                            target.onerror = null
                            target.src = missingImg[TranslationKeys.notFoundImgPath]
                          }}
                          alt=''
                        />
                      </Col>
                    )
                  })
                ) : (
                  <Col></Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  )
}

export default ImagesGallery
